import React, { useState } from 'react';
import { 
  Boxes, 
  Zap, 
  FileText, 
  LayoutGrid, 
  ArrowRight, 
  ArrowLeft, 
  Check, 
  X 
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { safeStorage } from '../../utils/safeStorage';
import { useAuth } from '../../contexts/AuthContext';

interface WelcomeGuidedTourModalProps {
  isOpen: boolean;
  onClose: () => void;
  onStartFirstQuote: () => void;
  userName?: string;
  userId?: string;
}

export default function WelcomeGuidedTourModal({
  isOpen,
  onClose,
  onStartFirstQuote,
  userName = 'Empreendedor',
  userId = 'default'
}: WelcomeGuidedTourModalProps) {
  const { session } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);

  if (!isOpen) return null;

  const persistTourCompletion = async () => {
    try {
      safeStorage.setItem(`rmax_has_seen_tour_${userId}`, 'true');
      sessionStorage.setItem(`rmax_has_seen_tour_${userId}`, 'true');

      // Salvaguarda: atualização segura em nuvem apenas se houver sessão ativa
      if (session?.user?.id) {
        supabase.auth.updateUser({
          data: { has_seen_tour: true }
        }).catch((err) => {
          console.warn('[WelcomeGuidedTourModal] Persistência em nuvem ignorada silenciosamente:', err);
        });
      }
    } catch (e) {
      console.warn('[WelcomeGuidedTourModal] Erro ao gravar conclusão do tour:', e);
    }
  };

  const handleFinishAndQuote = async () => {
    await persistTourCompletion();
    onClose();
    onStartFirstQuote();
  };

  const handleFinishAndStay = async () => {
    await persistTourCompletion();
    onClose();
  };

  const steps = [
    {
      stepNumber: 1,
      badge: 'Passo 1 de 4',
      title: 'Materiais e Valor Hora',
      icon: Boxes,
      iconBg: 'bg-emerald-50 text-emerald-700 border-emerald-200',
      description: 'O segredo de não ter prejuízo começa na base.',
      details: [
        'Cadastre o custo unitário real dos seus materiais e insumos de trabalho.',
        'Defina o seu Valor Hora técnico com base nos seus custos fixos mensais e meta salarial.',
        'O R-Max utiliza esses números para proteger seu lucro real em cada serviço.'
      ]
    },
    {
      stepNumber: 2,
      badge: 'Passo 2 de 4',
      title: 'Criador de Orçamentos com Margem Protegida',
      icon: Zap,
      iconBg: 'bg-amber-50 text-amber-700 border-amber-200',
      description: 'Chega de calcular preços por intuição ou chutes arriscados.',
      details: [
        'Selecione os materiais utilizados e o tempo de execução previsto.',
        'O motor calcula o preço de venda exato com mark-up e arredondamento seguro.',
        'Você enxerga com total clareza o seu lucro líquido antes de apresentar o valor ao cliente.'
      ]
    },
    {
      stepNumber: 3,
      badge: 'Passo 3 de 4',
      title: 'Envio de Proposta Profissional em PDF',
      icon: FileText,
      iconBg: 'bg-blue-50 text-blue-700 border-blue-200',
      description: 'Passe máxima credibilidade no WhatsApp do cliente.',
      details: [
        'Gere uma proposta comercial limpa com o nome da sua empresa, telefone e dados do serviço.',
        'PDF pronto e formatado para leitura agradável na tela do celular do cliente.',
        'Compartilhe em instantes direto no WhatsApp com mensagem de envio organizada.'
      ]
    },
    {
      stepNumber: 4,
      badge: 'Passo 4 de 4',
      title: 'Navegação Fácil pelo Menu Inferior',
      icon: LayoutGrid,
      iconBg: 'bg-zinc-100 text-zinc-800 border-zinc-300',
      description: 'Você nunca fica perdido dentro do R-Max.',
      details: [
        'Use a barra no rodapé da tela para alternar entre Início, Orçar e Materiais.',
        'Acesse seu Painel a qualquer momento para conferir listas de texto estruturadas e histórico.',
        'O botão central (+) permite criar novos orçamentos e registros rapidamente de onde você estiver.'
      ]
    }
  ];

  const activeStep = steps[currentStep];
  const StepIcon = activeStep.icon;
  const isLastStep = currentStep === steps.length - 1;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-neutral-950/70 backdrop-blur-xs font-sans animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-modal-title"
    >
      {/* Card de Superfície Google Material 3 com Contraste Acentuado */}
      <div className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-zinc-200 overflow-hidden flex flex-col max-h-[92vh]">
        
        {/* Header Superior com Pílulas de Progresso */}
        <div className="p-4 sm:p-6 pb-3 border-b border-zinc-100 flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5 flex-1 max-w-xs">
            {steps.map((_, idx) => (
              <div 
                key={idx}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  idx === currentStep 
                    ? 'w-8 bg-zinc-900' 
                    : idx < currentStep 
                    ? 'w-4 bg-emerald-600' 
                    : 'w-2 bg-zinc-200'
                }`}
              />
            ))}
          </div>

          <button
            type="button"
            onClick={handleFinishAndStay}
            className="min-h-12 min-w-12 p-2 -mr-2 rounded-full hover:bg-zinc-100 text-zinc-500 hover:text-zinc-900 transition-colors flex items-center justify-center cursor-pointer"
            aria-label="Pular e fechar tour"
            title="Fechar tour"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Corpo do Conteúdo com Tipografia Legível para 40/50+ */}
        <div className="p-5 sm:p-6 overflow-y-auto flex-1 space-y-4">
          <div className="flex items-center gap-3">
            <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border shrink-0 ${activeStep.iconBg}`}>
              <StepIcon className="w-6 h-6 stroke-[2]" />
            </div>
            <div>
              <span className="text-[11px] font-black uppercase tracking-wider text-zinc-500 block">
                {activeStep.badge}
              </span>
              <h2 id="tour-modal-title" className="text-lg sm:text-xl font-black text-zinc-900 leading-tight">
                {activeStep.title}
              </h2>
            </div>
          </div>

          <p className="text-sm sm:text-base font-medium text-zinc-700 leading-relaxed">
            {activeStep.description}
          </p>

          <div className="bg-zinc-50 border border-zinc-200/80 rounded-2xl p-4 space-y-3">
            {activeStep.details.map((item, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div className="mt-1 w-4 h-4 rounded-full bg-emerald-100 text-emerald-800 flex items-center justify-center shrink-0">
                  <Check className="w-2.5 h-2.5 stroke-[3]" />
                </div>
                <p className="text-xs sm:text-sm text-zinc-700 leading-relaxed font-normal">
                  {item}
                </p>
              </div>
            ))}
          </div>

          {currentStep === 0 && (
            <p className="text-xs text-zinc-500 italic text-center pt-1">
              Bem-vindo ao R-Max, {userName.split(' ')[0]}! Este breve resumo leva apenas 1 minuto.
            </p>
          )}
        </div>

        {/* Barra de Ações Fixa na Base (M3 Fixed Bottom Action Bar) com Alvos Mínimos de 48dp */}
        <div className="p-4 sm:p-6 pt-3 bg-zinc-50/90 border-t border-zinc-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="w-full sm:w-auto flex items-center gap-2">
            {currentStep > 0 && (
              <button
                type="button"
                onClick={() => setCurrentStep(prev => Math.max(0, prev - 1))}
                className="min-h-12 px-4 py-2.5 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-100 text-zinc-700 font-bold text-xs uppercase tracking-wider transition-colors flex items-center justify-center gap-1.5 cursor-pointer flex-1 sm:flex-none"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Voltar</span>
              </button>
            )}

            <button
              type="button"
              onClick={handleFinishAndStay}
              className="min-h-12 px-3 py-2.5 text-zinc-500 hover:text-zinc-900 font-bold text-xs uppercase tracking-wider transition-colors flex items-center justify-center cursor-pointer"
            >
              Pular
            </button>
          </div>

          <div className="w-full sm:w-auto flex items-center gap-2">
            {!isLastStep ? (
              <button
                type="button"
                onClick={() => setCurrentStep(prev => Math.min(steps.length - 1, prev + 1))}
                className="w-full sm:w-auto min-h-12 px-6 py-2.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 text-white font-bold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-sm active:scale-95 cursor-pointer"
              >
                <span>Próximo Passo</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleFinishAndQuote}
                className="w-full sm:w-auto min-h-12 px-6 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-md shadow-emerald-600/20 active:scale-95 cursor-pointer"
              >
                <Zap className="w-4 h-4" />
                <span>Criar Primeiro Orçamento</span>
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
